import useUrlState from "@/hooks/useUrlState";

export interface ChartDataPoint {
  day: string;
  sms: number;
  lms: number;
  mms: number;
}

interface SendChartProps {
  weeklyData: ChartDataPoint[];
  monthlyData: ChartDataPoint[];
  loading?: boolean;
}

type Period = "week" | "month";

export default function SendChart({ weeklyData, monthlyData, loading }: SendChartProps) {
  // 차트 기간(주간/월간) 선택을 주소(URL)에 남겨 새로고침·링크 공유·뒤로 가기에도 유지
  const [periodParam, setPeriodParam] = useUrlState("chart_period", "week");
  const period: Period = periodParam === "month" ? "month" : "week";
  const setPeriod = (p: Period) => setPeriodParam(p);
  const data = period === "week" ? weeklyData : monthlyData;

  const maxVal = data.length > 0 ? Math.max(...data.map((d) => d.sms + d.lms + d.mms)) : 1;

  return (
    <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-white font-semibold text-base">발송 현황</h3>
          <p className="text-gray-500 text-xs mt-0.5">유형별 발송량 추이</p>
        </div>
        <div className="flex bg-white/[0.05] rounded-lg p-0.5">
          {(["week", "month"] as Period[]).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer whitespace-nowrap transition-all ${
                period === p ? "bg-white/[0.1] text-white" : "text-gray-500 hover:text-gray-300"
              }`}
            >
              {p === "week" ? "주간" : "월간"}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="h-40 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-violet-500/40 border-t-violet-500 rounded-full animate-spin"></div>
        </div>
      ) : (
        <>
          {/* Chart */}
          <div className="flex items-end gap-2 h-40">
            {data.map((d) => {
              const total = d.sms + d.lms + d.mms;
              const smsH = maxVal > 0 ? (d.sms / maxVal) * 100 : 0;
              const lmsH = maxVal > 0 ? (d.lms / maxVal) * 100 : 0;
              const mmsH = maxVal > 0 ? (d.mms / maxVal) * 100 : 0;
              return (
                <div key={d.day} className="flex-1 flex flex-col items-center gap-1 group">
                  <div className="w-full flex flex-col justify-end h-32 gap-0.5 relative">
                    {/* Tooltip */}
                    <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-[#1a1a2e] border border-white/[0.1] rounded-lg px-2.5 py-1.5 text-xs text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                      <p className="font-semibold">{total.toLocaleString()}건</p>
                    </div>
                    {/* MMS bar */}
                    <div
                      className="w-full rounded-t-sm bg-amber-500/60 transition-all duration-500"
                      style={{ height: `${mmsH}%` }}
                    ></div>
                    {/* LMS bar */}
                    <div
                      className="w-full bg-pink-500/60 transition-all duration-500"
                      style={{ height: `${lmsH}%` }}
                    ></div>
                    {/* SMS bar */}
                    <div
                      className="w-full rounded-b-sm bg-violet-500/70 transition-all duration-500"
                      style={{ height: `${smsH}%` }}
                    ></div>
                  </div>
                  <span className="text-gray-500 text-xs">{d.day}</span>
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 mt-4 pt-4 border-t border-white/[0.06]">
            {[
              { color: "bg-violet-500/70", label: "SMS" },
              { color: "bg-pink-500/60", label: "LMS" },
              { color: "bg-amber-500/60", label: "MMS" },
            ].map((l) => (
              <div key={l.label} className="flex items-center gap-1.5">
                <div className={`w-2.5 h-2.5 rounded-sm ${l.color}`}></div>
                <span className="text-gray-400 text-xs">{l.label}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}